import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, XCircle, CheckCircle, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, Question, GameStats, Operation, Language } from '../types';
import QuestionDisplay from './QuestionDisplay';
import AnswerInput from './AnswerInput';
import ProgressBar from './ProgressBar';
import GameSummary from './GameSummary';
import BadgeNotification from './BadgeNotification';
import useSound from 'use-sound';
import errorSound from '../assets/sounds/error2.mp3';
import successSound from '../assets/sounds/success.wav';

interface MultiplicationGameProps {
  mode: GameMode;
  operation: Operation;
  onReset: () => void;
  language: Language;
}

const MultiplicationGame: React.FC<MultiplicationGameProps> = ({ mode, operation, onReset, language }) => {
  const [selectedTable, setSelectedTable] = useState<number | null>(null);
  const [currentTable, setCurrentTable] = useState(1);
  const [currentMultiplier, setCurrentMultiplier] = useState(1);
  const [question, setQuestion] = useState<Question | null>(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [answerStatus, setAnswerStatus] = useState<'correct' | 'incorrect' | null>(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [remainingTime, setRemainingTime] = useState(60);
  const [startTime, setStartTime] = useState(Date.now());
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationTriggered, setCelebrationTriggered] = useState(false); // pour éviter de le déclencher 2x

  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });
  const [newBadge, setNewBadge] = useState<any>(null);
  const [showTableComplete, setShowTableComplete] = useState(false);

  const [playError] = useSound(errorSound, { volume: 0.3 });
  const [playSuccess] = useSound(successSound, { volume: 0.5 });

  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<number | null>(null);
  const firstExerciseRef = useRef(false);

  const checkAndAwardBadges = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userBadges } = await supabase
        .from('user_badges')
        .select('badge_id')
        .eq('user_id', user.id);

      const earnedBadgeIds = new Set(userBadges?.map(ub => ub.badge_id) || []);

      const { data: badges } = await supabase
        .from('badges')
        .select('*');

      if (!badges) return;

      for (const badge of badges) {
        if (earnedBadgeIds.has(badge.id)) continue;

        const criteria = badge.criteria as any;
        let shouldAward = false;

        switch (criteria.type) {
          case 'first_exercise':
            shouldAward = !firstExerciseRef.current;
            break;
          case 'perfect_score':
            shouldAward = currentStats.questionsAnswered >= 5 && 
              currentStats.correctAnswers === currentStats.questionsAnswered;
            break;
          case 'timed_exercises':
            if (mode === 'timed') {
              const { count: timedCount } = await supabase
                .from('user_progress')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', user.id)
                .eq('mode', 'timed');
              shouldAward = (timedCount || 0) >= criteria.count;
            }
            break;
        }

        if (shouldAward) {
          await supabase
            .from('user_badges')
            .insert({
              user_id: user.id,
              badge_id: badge.id,
            });
          
          setNewBadge(badge);
        }
      }

      firstExerciseRef.current = true;
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  };

  const generateQuestion = () => {
    let multiplicand: number;
    let multiplier: number;
    let correctAnswer: number;

    if (operation === 'multiplication') {
      if (mode === 'sequential') {
        multiplicand = selectedTable || currentTable;
        multiplier = currentMultiplier;
      } else {
        multiplicand = Math.floor(Math.random() * 12) + 1;
        multiplier = Math.floor(Math.random() * 12) + 1;
      }
      correctAnswer = multiplicand * multiplier;
    } else {
      if (mode === 'sequential') {
        multiplier = selectedTable || currentTable;
        correctAnswer = currentMultiplier;
        multiplicand = multiplier * correctAnswer;
      } else {
        multiplier = Math.floor(Math.random() * 12) + 1;
        correctAnswer = Math.floor(Math.random() * 12) + 1;
        multiplicand = multiplier * correctAnswer;
      }
    }

    const newQuestion: Question = {
      multiplicand,
      multiplier,
      correctAnswer,
      operation,
    };

    setQuestion(newQuestion);
    setUserAnswer('');
    setAnswerStatus(null);
    setStartTime(Date.now());

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  useEffect(() => {
    if (showCelebration) {
      playSuccess(); // Le son sera tenté une fois showCelebration est true
    }
  }, [showCelebration]);

  useEffect(() => {
    if ((mode !== 'sequential') || (mode === 'sequential' && selectedTable === null)) {
      generateQuestion();
    }

    if (mode === 'timed') {
      timerRef.current = window.setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1 && !celebrationTriggered) {
            clearInterval(timerRef.current!);
            setShowCelebration(true);
            setCelebrationTriggered(true);
            setTimeout(() => {
              setGameComplete(true);
              setShowCelebration(false);
            }, 1500); // délai d'affichage avant le résumé
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [mode, operation, selectedTable]);

  useEffect(() => {
    if (mode === 'sequential' && selectedTable !== null) {
      setCurrentTable(selectedTable);
      setCurrentMultiplier(1);
    }
  }, [selectedTable]);

  useEffect(() => {
    if (
      mode === 'sequential' &&
      selectedTable !== null &&
      currentTable === selectedTable &&
      currentMultiplier >= 1 &&
      currentMultiplier <= 12
    ) {
      generateQuestion();
    }
  }, [currentTable, currentMultiplier, selectedTable]);

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No user found');
        return;
      }

      const { error } = await supabase
        .from('user_progress')
        .insert({
          user_id: user.id,
          operation,
          mode,
          questions_answered: currentStats.questionsAnswered,
          correct_answers: currentStats.correctAnswers,
          average_response_time: currentStats.averageResponseTime,
        });

      if (error) {
        console.error('Error saving progress:', error);
      }

      await checkAndAwardBadges(currentStats);
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const handleSubmitAnswer = () => {
    if (!question || userAnswer.trim() === '') return;

    const endTime = Date.now();
    const responseTime = (endTime - startTime) / 1000;
    const numericAnswer = parseInt(userAnswer, 10);
    const isCorrect = numericAnswer === question.correctAnswer;
    
    setAnswerStatus(isCorrect ? 'correct' : 'incorrect');

    if (!isCorrect) {
      playError();
    }

    const newStats = {
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: isCorrect ? stats.correctAnswers + 1 : stats.correctAnswers,
      incorrectAnswers: isCorrect ? stats.incorrectAnswers : stats.incorrectAnswers + 1,
      totalTime: stats.totalTime + responseTime,
      averageResponseTime: 0,
    };
    newStats.averageResponseTime = newStats.totalTime / newStats.questionsAnswered;
    
    setStats(newStats);

    if (mode === 'timed') {
      saveProgress(newStats);
    }

    setTimeout(() => {
      if (mode === 'sequential') {
        if (currentMultiplier < 12) {
          setCurrentMultiplier((prev) => prev + 1);
        } else {
          setShowTableComplete(true);
          playSuccess();
          setTimeout(() => {
            setShowTableComplete(false);
            setSelectedTable(null);
          }, 3000); // Increased to 3 seconds
        }
      }

      if (mode === 'timed' && remainingTime <= 0) {
        playSuccess();
        setGameComplete(true);
        saveProgress(newStats);
        return;
      }

      generateQuestion();
    }, 1000);
  };

  const handleInputChange = (value: string) => {
    setUserAnswer(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmitAnswer();
    }
  };

  if (mode === 'sequential' && !selectedTable) {
    return (
      <div className="w-full max-w-2xl">
        <div className="mb-6 flex items-center">
          <button
            onClick={onReset}
            className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-6 text-center">
            {language === 'en' 
              ? `Choose a ${operation} table to practice`
              : `Choisissez une table de ${operation === 'multiplication' ? 'multiplication' : 'division'} à pratiquer`}
          </h2>

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                onClick={() => setSelectedTable(num)}
                className="aspect-square bg-slate-50 dark:bg-slate-700 rounded-lg text-2xl font-bold 
                  text-slate-700 dark:text-slate-200
                  hover:bg-indigo-50 dark:hover:bg-indigo-900 hover:text-indigo-600 dark:hover:text-indigo-300 
                  transition-colors"
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-6 flex items-center">
        <button
          onClick={mode === 'sequential' ? () => setSelectedTable(null) : onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {mode === 'sequential'
            ? (language === 'en' ? 'Choose Another Table' : 'Choisir une autre table')
            : (language === 'en' ? 'Back to Modes' : 'Retour aux modes')}
        </button>
        
        <div className="ml-auto text-lg font-medium">
          {mode === 'sequential' ? (
            <span className="text-indigo-700 dark:text-indigo-300">
              {language === 'en' ? 'Table' : 'Table'} {selectedTable || currentTable}
            </span>
          ) : mode === 'timed' ? (
            <span className={`font-bold ${remainingTime <= 10 ? 'text-red-600 dark:text-red-400' : 'text-indigo-700 dark:text-indigo-300'}`}>
              {remainingTime}s
            </span>
          ) : (
            <span className="text-indigo-700 dark:text-indigo-300">
              {language === 'en' ? 'Random Practice' : 'Pratique aléatoire'}
            </span>
          )}
        </div>
      </div>

      {showTableComplete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center shadow-xl transform scale-95 opacity-0 animate-[fadeZoomIn_0.5s_ease-out_forwards]">
            <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {language === 'en' ? 'Well Done!' : 'Bravo !'}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300">
              {language === 'en' 
                ? `You've completed the ${operation === 'multiplication' ? 'multiplication' : 'division'} table of ${currentTable}!`
                : `Vous avez terminé la table de ${operation === 'multiplication' ? 'multiplication' : 'division'} de ${currentTable} !`}
            </p>
          </div>
        </div>
      )}

      {showCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="text-6xl animate-bounce text-yellow-400">🎉</div>
          {/* Tu peux remplacer ceci par une vraie animation si tu veux plus tard */}
        </div>
      )}
      
      {gameComplete ? (
        <GameSummary 
          stats={stats} 
          mode={mode} 
          operation={operation}
          onPlayAgain={() => {
            setGameComplete(false);
            setCelebrationTriggered(false);
            setCurrentTable(1);
            setCurrentMultiplier(1);
            setRemainingTime(60);
            setStats({
              questionsAnswered: 0,
              correctAnswers: 0,
              incorrectAnswers: 0,
              totalTime: 0,
              averageResponseTime: 0,
            });
            generateQuestion();
            
            if (mode === 'timed') {
              timerRef.current = window.setInterval(() => {
                setRemainingTime((prev) => {
                  if (prev <= 1) {
                    clearInterval(timerRef.current!);
                    playSuccess();
                    setGameComplete(true);
                    return 0;
                  }
                  return prev - 1;
                });
              }, 1000);
            }
          }} 
        />
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
          {mode === 'sequential' && (
            <ProgressBar currentMultiplier={currentMultiplier} />
          )}
          
          {question && (
            <>
              <QuestionDisplay question={question} />
              
              <div className="mt-6">
                <AnswerInput
                  value={userAnswer}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  onSubmit={handleSubmitAnswer}
                  status={answerStatus}
                  ref={inputRef}
                />
              </div>
              
              {answerStatus && (
                <div className={`mt-4 flex items-center ${
                  answerStatus === 'correct' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {answerStatus === 'correct' ? (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2" />
                      <span>{language === 'en' ? 'Correct! Great job.' : 'Correct ! Bon travail.'}</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 mr-2" />
                      <span>
                        {language === 'en' 
                          ? `Not quite. The correct answer is ${question.correctAnswer}.`
                          : `Pas tout à fait. La bonne réponse est ${question.correctAnswer}.`}
                      </span>
                    </>
                  )}
                </div>
              )}
              
              <div className="mt-6 text-sm text-slate-500 dark:text-slate-400">
                {stats.questionsAnswered > 0 && (
                  <div className="flex justify-between">
                    <span>{language === 'en' ? 'Questions' : 'Questions'}: {stats.questionsAnswered}</span>
                    <span className="text-green-600">{language === 'en' ? 'Correct' : 'Correct'}: {stats.correctAnswers}</span>
                    <span className={`font-bold ${(stats.correctAnswers / stats.questionsAnswered) * 100 < 60 ? 'text-red-500' : 'text-green-500'}`}>
                      {language === 'en' ? 'Accuracy' : 'Précision'}: {Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      
      {newBadge && (
        <BadgeNotification
          badge={newBadge}
          language={language}
          onClose={() => setNewBadge(null)}
        />
      )}
    </div>
  );
};

export default MultiplicationGame;